export type StoreLayout = 'classic' | 'centered' | 'magazine' | 'gallery'
export type ColorScheme =
  | 'pokemon'
  | 'midnight'
  | 'ember'
  | 'mint'
  | 'rose'
  | 'void'
  | 'electric'
  | 'grape'
  | 'ocean'
  | 'sakura'
  | 'steel'
  | 'lava'

export interface StoreTheme {
  page: string
  surface: string
  accent: string
  accentInk: string
  primary: string
  secondary: string
  overlay: string
}

export const STORE_LAYOUTS: Array<{
  value: StoreLayout
  label: string
  description: string
}> = [
  { value: 'classic', label: 'Classic', description: 'Banner header with a tight 3-column photo grid' },
  { value: 'centered', label: 'Centered', description: 'Centered avatar, stacked bio, roomier card grid' },
  { value: 'magazine', label: 'Magazine', description: 'Wide editorial header with a 2-column listing grid' },
  { value: 'gallery', label: 'Gallery', description: 'Compact header and a spacious collector wall' },
]

export const COLOR_SCHEMES: Array<{
  value: ColorScheme
  label: string
  description: string
  theme: StoreTheme
}> = [
  {
    value: 'pokemon',
    label: 'Poke Pack',
    description: 'Red, royal blue, and yellow',
    theme: {
      page: '#050505',
      surface: '#111111',
      accent: '#FFDE00',
      accentInk: '#111111',
      primary: '#FF1A1A',
      secondary: '#3B4CCA',
      overlay: 'rgba(0,0,0,0.62)',
    },
  },
  {
    value: 'midnight',
    label: 'Midnight',
    description: 'Navy, ice, and silver',
    theme: {
      page: '#07111d',
      surface: '#102033',
      accent: '#8BE9FF',
      accentInk: '#07111d',
      primary: '#3D7EFF',
      secondary: '#1B3B63',
      overlay: 'rgba(4,10,20,0.72)',
    },
  },
  {
    value: 'ember',
    label: 'Ember',
    description: 'Charcoal, flame, and gold',
    theme: {
      page: '#140904',
      surface: '#24140c',
      accent: '#FFB020',
      accentInk: '#1a0d05',
      primary: '#FF5A1F',
      secondary: '#8A3414',
      overlay: 'rgba(18,8,3,0.7)',
    },
  },
  {
    value: 'mint',
    label: 'Mint Leaf',
    description: 'Forest, mint, and cream',
    theme: {
      page: '#07140f',
      surface: '#10241c',
      accent: '#7DFFC3',
      accentInk: '#062017',
      primary: '#1FA971',
      secondary: '#145C44',
      overlay: 'rgba(4,16,12,0.7)',
    },
  },
  {
    value: 'rose',
    label: 'Rose Gold',
    description: 'Wine, blush, and gold',
    theme: {
      page: '#16080d',
      surface: '#2a1219',
      accent: '#F4C7B0',
      accentInk: '#2a1219',
      primary: '#E23B6A',
      secondary: '#7A2744',
      overlay: 'rgba(18,6,10,0.7)',
    },
  },
  {
    value: 'void',
    label: 'Void',
    description: 'Black, white, and graphite',
    theme: {
      page: '#050505',
      surface: '#141414',
      accent: '#F4F4F4',
      accentInk: '#111111',
      primary: '#FFFFFF',
      secondary: '#4A4A4A',
      overlay: 'rgba(0,0,0,0.68)',
    },
  },
  {
    value: 'electric',
    label: 'Electric',
    description: 'Ink, lemon, and storm gray',
    theme: {
      page: '#090901',
      surface: '#16140a',
      accent: '#FFE14A',
      accentInk: '#17150a',
      primary: '#FFDE00',
      secondary: '#5C5330',
      overlay: 'rgba(8,8,2,0.68)',
    },
  },
  {
    value: 'grape',
    label: 'Grape Soda',
    description: 'Violet, lilac, and ink',
    theme: {
      page: '#0d0816',
      surface: '#1b1228',
      accent: '#D7B3FF',
      accentInk: '#1b1228',
      primary: '#8B5CFF',
      secondary: '#4A2B86',
      overlay: 'rgba(10,6,18,0.7)',
    },
  },
  {
    value: 'ocean',
    label: 'Ocean Spray',
    description: 'Teal, aqua, and deep navy',
    theme: {
      page: '#041318',
      surface: '#0d2430',
      accent: '#7CF0E4',
      accentInk: '#062026',
      primary: '#19B8C7',
      secondary: '#14586A',
      overlay: 'rgba(3,14,18,0.7)',
    },
  },
  {
    value: 'sakura',
    label: 'Sakura',
    description: 'Blush, cream, and cherry',
    theme: {
      page: '#160910',
      surface: '#2a1520',
      accent: '#FFD6E4',
      accentInk: '#2a1520',
      primary: '#FF6B9D',
      secondary: '#8A3A58',
      overlay: 'rgba(18,7,12,0.68)',
    },
  },
  {
    value: 'steel',
    label: 'Steel',
    description: 'Silver, slate, and ice',
    theme: {
      page: '#0c1014',
      surface: '#1a2128',
      accent: '#D7E2EC',
      accentInk: '#12171c',
      primary: '#8EA3B8',
      secondary: '#3D4C5A',
      overlay: 'rgba(8,10,13,0.7)',
    },
  },
  {
    value: 'lava',
    label: 'Lava',
    description: 'Crimson, ember, and ash',
    theme: {
      page: '#120505',
      surface: '#241010',
      accent: '#FF8A4A',
      accentInk: '#1a0a08',
      primary: '#E10600',
      secondary: '#6B1A16',
      overlay: 'rgba(16,4,4,0.7)',
    },
  },
]

export const BACKGROUND_IMAGE_HINT = 'Best size: 1920 x 1080 px (16:9). Keep it under 10MB.'
export const BANNER_IMAGE_HINT = 'Best size: 1500 x 500 px (3:1). Keep it under 10MB.'

export function getStoreLayout(value?: string | null): StoreLayout {
  return STORE_LAYOUTS.some(item => item.value === value) ? (value as StoreLayout) : 'classic'
}

export function getColorScheme(value?: string | null): ColorScheme {
  return COLOR_SCHEMES.some(item => item.value === value) ? (value as ColorScheme) : 'pokemon'
}

export function getStoreTheme(value?: string | null): StoreTheme {
  const scheme = getColorScheme(value)
  return COLOR_SCHEMES.find(item => item.value === scheme)?.theme || COLOR_SCHEMES[0].theme
}

export function getColorSchemeOption(value?: string | null) {
  const scheme = getColorScheme(value)
  return COLOR_SCHEMES.find(item => item.value === scheme) || COLOR_SCHEMES[0]
}
